import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import {
  FaEye, FaComment, FaInfoCircle,
  FaSearch, FaTimes, FaUndo,
  FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt,
  FaClock, FaCalendarAlt, FaUsers, FaTag, FaCog,
  FaPlus, FaTrash, FaSync
} from 'react-icons/fa';
import { leadSources, leadStatuses, getLeadSourceById, getStatusById } from '../data/mockData';
import LeadDetailView from './LeadDetailView';
import apiService from '../services/api';

// Helper function to get lead type based on source
const getLeadType = (sourceId) => {
  const typeMap = {
    'website': 'Web',
    'meta_ads': 'Social',
    'google_ads': 'Search',
    'manual': 'Manual'
  };
  return typeMap[sourceId] || 'Unknown';
};

const Container = styled.div`
  padding: 24px;
  background: #fafbfc;
  min-height: 100vh;
  color: #1a202c;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', system-ui, sans-serif;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 1680px) {
    padding: 20px;
  }

  @media (max-width: 1600px) {
    padding: 16px;
  }

  @media (max-width: 1440px) {
    padding: 12px;
  }

  @media (max-width: 1280px) {
    padding: 10px;
  }

  @media (max-width: 1024px) {
    padding: 8px;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const Header = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px 32px;
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid #f1f5f9;

  @media (max-width: 1680px) {
    padding: 20px 24px;
    margin-bottom: 24px;
    gap: 20px;
  }

  @media (max-width: 1600px) {
    padding: 16px 20px;
    margin-bottom: 20px;
    gap: 16px;
  }

  @media (max-width: 1440px) {
    padding: 12px 16px;
    margin-bottom: 16px;
    gap: 12px;
  }

  @media (max-width: 1280px) {
    padding: 10px 12px;
    margin-bottom: 12px;
    gap: 8px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-rows: repeat(1, 1fr);
  gap: 1rem;
  width: 100%;
`;

const GridColumnSearch = styled.div`
  grid-column: span 2;
`;

const Title = styled.h1`
  color: #1a202c;
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 12px;
  letter-spacing: -0.5px;
  line-height: 1.2;

  @media (max-width: 1680px) {
    font-size: 28px;
  }

  @media (max-width: 1600px) {
    font-size: 24px;
  }

  @media (max-width: 1440px) {
    font-size: 20px;
  }

  @media (max-width: 1280px) {
    font-size: 18px;
  }
`;

const FilterSection = styled.div`
  display: flex;
  grid-column: span 10;
  justify-content: flex-end;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 1680px) {
    gap: 10px;
  }

  @media (max-width: 1600px) {
    gap: 8px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FilterLabel = styled.label`
  color: #4a5568;
  font-size: 14px;
  font-weight: 500;

  @media (max-width: 1680px) {
    font-size: 13px;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
  }

  @media (max-width: 1440px) {
    font-size: 10px;
  }

  @media (max-width: 1280px) {
    font-size: 8px;
  }
`;

const InputSelectDate = styled.input`
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #2d3748;
  font-size: 14px;
  min-width: 160px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }

  &:hover {
    border-color: #cbd5e0;
  }

  @media (max-width: 1680px) {
    min-width: 120px;
    padding: 10px;
    font-size: 13px;
  }

  @media (max-width: 1600px) {
    min-width: 80px;
    padding: 8px;
    font-size: 12px;
  }

  @media (max-width: 1440px) {
    min-width: 60px;
    padding: 6px;
    font-size: 11px;
  }

  @media (max-width: 1280px) {
    min-width: 40px;
    padding: 4px;
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #2d3748;
  font-size: 14px;
  min-width: 160px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }

  @media (max-width: 1680px) {
    min-width: 120px;
    padding: 8px;
    font-size: 13px;
  }

  @media (max-width: 1600px) {
    min-width: 80px;
    font-size: 12px;
  }

  @media (max-width: 1440px) {
    min-width: 80px;
    font-size: 11px;
  }

  @media (max-width: 1280px) {
    min-width: 60px;
    font-size: 10px;
  }
`;

const SearchBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 12px 12px 12px 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #2d3748;
  font-size: 14px;
  width: 300px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }
  
  &::placeholder {
    color: #a0aec0;
  }

  @media (max-width: 1680px) {
    width: 200px;
    padding: 8px 8px 8px 35px;
    font-size: 13px;
  }

  @media (max-width: 1600px) {
    width: 150px;
    font-size: 12px;
  }

  @media (max-width: 1440px) {
    width: 100%;
    font-size: 11px;
  }
  
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 12px;
  color: #666;
  font-size: 16px;
`;

const ResetButton = styled.button`
  padding: 12px 20px;
  background: linear-gradient(135deg, #6b7280, #4b5563);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  margin-top: 16px;
  
  &:hover {
    background: linear-gradient(135deg, #4b5563, #374151);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(75, 85, 99, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }

  @media (max-width: 1680px) {
    padding: 10px;
    gap: 8px;
    font-size: 13px;
  }

  @media (max-width: 1600px) {
    gap: 7px;
    font-size: 12px;
  }

  @media (max-width: 1440px) {
    gap: 6px;
    font-size: 11px;
  }

  @media (max-width: 1280px) {
    gap: 5px;
    font-size: 10px;
  }
`;

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 20px;
  background: white;
  border-radius: 16px;
  margin-top: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid #f1f5f9;
`;

const LoadMoreButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #4299e1, #3182ce);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #3182ce, #2c5aa0);
    transform: translateY(-1px);
    box-shadow: 0 8px 25px rgba(66, 153, 225, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const PaginationInfo = styled.div`
  text-align: center;
  color: #64748b;
  font-size: 14px;
  margin-bottom: 16px;
`;

const BulkActionsContainer = styled.div`
  margin-bottom: 24px;
`;

const BulkActionsCard = styled.div`
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
`;

const BulkActionsTitle = styled.div`
  color: #2d3748;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BulkActionsButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const BulkButton = styled.button`
  padding: 12px 20px;
  background: ${props => props.danger ? '#f56565' : '#48bb78'};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${props => props.danger
    ? 'rgba(245, 101, 101, 0.3)'
    : 'rgba(72, 187, 120, 0.3)'
  };
    background: ${props => props.danger ? '#e53e3e' : '#38a169'};
  }
`;

const NotificationContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from { 
      transform: translateX(100%) scale(0.95);
      opacity: 0;
    }
    to { 
      transform: translateX(0) scale(1);
      opacity: 1;
    }
  }
`;

const NotificationCard = styled.div`
  background: ${props => {
    switch (props.type) {
      case 'success': return 'white';
      case 'error': return 'white';
      case 'info': return 'white';
      default: return 'white';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'success': return '#059669';
      case 'error': return '#dc2626';
      case 'info': return '#2563eb';
      default: return '#059669';
    }
  }};
  border-left: 4px solid ${props => {
    switch (props.type) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'info': return '#3b82f6';
      default: return '#10b981';
    }
  }};
  padding: 14px 20px;
  border-radius: 10px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 300px;
  
  &::before {
    content: ${props => {
    switch (props.type) {
      case 'success': return '"✓"';
      case 'error': return '"✕"';
      case 'info': return '"ℹ"';
      default: return '"✓"';
    }
  }};
    font-weight: bold;
    font-size: 14px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${props => {
    switch (props.type) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'info': return '#3b82f6';
      default: return '#10b981';
    }
  }};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  }

  h3 {
    margin: 0 0 8px 0;
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.5px;
  }
  
  p {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
  }
`;

const LeadsTable = styled.div`
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: ${props => props.isAdmin ? '60px' : ''} 1fr 1fr 1fr 1fr 1fr;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
  padding: 14px 10px;
  font-weight: 700;
  font-size: 14px;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  gap: 12px;
  align-items: center;

  @media (max-width: 1680px) {
    font-size: 13px;
    gap: 10px;
    padding: 10px 10px;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    gap: 8px;
    padding: 8px 8px;
  }

  @media (max-width: 1440px) {
    font-size: 11px;
    gap: 6px;
    padding: 6px 6px;
  }

  @media (max-width: 1280px) {
    font-size: 10px;
    gap: 4px;
    padding: 4px 4px;
  }
`;

const TitlesPadding = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding-left: ${props => props.owner ? '20px' : '10px'};
`;

const TableHeaderCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  &:first-child {
    justify-content: ${props => props.isAdmin ? 'center' : 'left'};
    padding-left: ${props => props.isAdmin ? '0' : '10px'};
  }

  @media (max-width: 1680px) {
    gap: 10px;
    font-size: 13px;

    &:first-child {
      justify-content: ${props => props.isAdmin ? 'center' : 'left'};
      padding-left: ${props => props.isAdmin ? '0' : '10px'};
    }
  }

  @media (max-width: 1600px) {
    gap: 8px;
    font-size: 12px;
  }

  @media (max-width: 1440px) {
    gap: 6px;
    font-size: 11px;
  }

  @media (max-width: 1280px) {
    gap: 4px;
    font-size: 10px;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.isAdmin ? '60px' : ''} 1fr 1fr 1fr 1fr 1fr;
  padding: 14px 10px;
  border-bottom: 1px solid #f1f5f9;
  gap: 12px;
  position: relative;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover {
    background: #e8f2fd;
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${props => props.statusColor || '#4299e1'};
  }
  
  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 1680px) {
    font-size: 13px;
    gap: 10px;
    padding: 10px 10px;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    gap: 8px;
    padding: 8px 8px;
  }

  @media (max-width: 1440px) {
    font-size: 11px;
    gap: 6px;
    padding: 6px 6px;
  }

  @media (max-width: 1280px) {
    gap: 4px;
    font-size: 10px;
    padding: 4px 4px;
  }
`;

const TableCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #374151;
  min-width: 0; 
  font-size: 14px;
  
  &:first-child {
    align-items: center;
    padding-left: ${props => props.isAdmin ? '0' : '10px'};
  }
  
  /* Lead Info column (first column after checkbox) */
  &:nth-child(${props => props.isAdmin ? '2' : '1'}) {
    width: 85%;
  }
  
  /* Actions column (last column) */
  &:last-child {
    width: 85%;
  }

  @media (max-width: 1680px) {
    font-size: 13px;
    gap: 10px;
    padding: 0;

    &:first-child {
      padding-left: ${props => props.isAdmin ? '0' : '10px'};
    }

    &:nth-child(${props => props.isAdmin ? '2' : '1'}) {
      width: 70%;
    }

    &:nth-child(${props => props.isAdmin ? '3' : '2'}) {
      width: 100%;
    }
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    gap: 8px;

    &:first-child {
      padding-left: ${props => props.isAdmin ? '0' : '10px'};
    }

    &:nth-child(${props => props.isAdmin ? '3' : '2'}) {
      width: 100%;
    }

    &:nth-child(${props => props.isAdmin ? '2' : '1'}) {
      width: 100%;
    }
  }

  @media (max-width: 1440px) {
    font-size: 11px;
    gap: 6px;

    &:nth-child(${props => props.isAdmin ? '3' : '2'}) {
      width: 100%;
    }

    &:nth-child(${props => props.isAdmin ? '2' : '1'}) {
      width: 100%;
    }
  }

  @media (max-width: 1280px) {
    gap: 4px;
    font-size: 10px;
  }
`;

// Column 1: Lead Info (ID, Source, Date, Time)
const LeadInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 14px;
  padding: 0;

  @media (max-width: 1680px) {
    font-size: 13px;
    gap: 0;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    gap: 0;
  }

  @media (max-width: 1440px) {
    font-size: 11px;
    gap: 0;
  }

  @media (max-width: 1280px) {
    gap: 4px;
  }
`;

const InfoGridBandage = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  gap: 0.625rem;
  padding: 0;
  width: 100%;

  @media (max-width: 1440px) {
    font-size: 9px;
    gap: 4px;
    width: 80%;
    border-radius: 8px;
  }
`;

const LeadIdContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
`;

const LeadTypeBadge = styled.span`
  background: #e5e7eb;
  color: #374151;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const LeadIdBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  // light blue
  background: #e8f2fd;
  width: max-content;
  color: #4299e1;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  border: 1px solid #4299e1;

  @media (max-width: 1680px) {
    font-size: 13px;
    padding: 6px 8px;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    padding: 4px 6px;
  }

  @media (max-width: 1440px) {
    font-size: 9px;
    padding: 4px 6px;
    gap: 4px;
  }

  @media (max-width: 1280px) {
    font-size: 8px;
    padding: 2px 4px;
    gap: 4px;
  }
`;

const LeadDuplicateBadge = styled.div`
  background: #ff6b6b20;
  color: #ff6b6b;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  width: max-content;
  text-align: center;
  border: 1px solid #ff6b6b;
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 1680px) {
    font-size: 13px;
    padding: 6px 6px;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    padding: 4px 4px;
  }

  @media (max-width: 1440px) {
    font-size: 9px;
    padding: 4px 4px;
    gap: 4px;
  }

  @media (max-width: 1280px) {
    font-size: 10px;
    padding: 2px 2px;
    gap: 2px;
  }
`;

const StatusBadgeSource = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: ${props => props?.color ? `${props?.color}` : '#f8fafc'};
  border: 1px solid ${props => props?.color ? `${props?.color}` : '#e2e8f0'};
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  width: fit-content;
  margin-bottom: 6px;

  @media (max-width: 1680px) {
    font-size: 13px;
    padding: 6px 6px;
    gap: 4px;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    padding: 4px 6px;
    gap: 4px;
  }

  @media (max-width: 1440px) {
    font-size: 9px;
    padding: 4px 6px;
    gap: 4px;
    border-radius: 8px;
  }

  @media (max-width: 1280px) {
    font-size: 10px;
    padding: 2px 4px;
    gap: 0px;
    border-radius: 8px;
  }
`;

const SourceBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: ${props => props.color ? `${props.color}15` : '#f8fafc'};
  border: 1px solid ${props => props.color ? `${props.color}` : '#e2e8f0'};
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.color || '#64748b'};
  width: fit-content;
  margin-bottom: 6px;

  @media (max-width: 1680px) {
    font-size: 13px;
    padding: 6px 8px;
    gap: 4px;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    padding: 4px 6px;
    gap: 2px;
  }

  @media (max-width: 1440px) {
    font-size: 9px;
    padding: 4px 6px;
    gap: 4px;
    border-radius: 8px;
  }

  @media (max-width: 1280px) {
    font-size: 10px;
    padding: 2px 4px;
    gap: 0px;
  }
`;

const DateTimeInfo = styled.div`
  font-size: 14px;
  color: #6b7280;
  gap: 10px;
  margin-top: 5px;
  display: flex;
  align-items: center;
  
  div {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;

    @media(max-width: 1680px) {
      font-size: 12px;
    }

    @media(max-width: 1600px) {
      font-size: 11px;
    }

    @media(max-width: 1440px) {
      font-size: 10px;
    }

    @media(max-width: 1280px) {
      font-size: 9px;
    }
  }

  @media (max-width: 1680px) {
    font-size: 13px;
    gap: 10px;
    font-size: 13px;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    gap: 10px;
    font-size: 12px;
  }

  @media (max-width: 1440px) {
    font-size: 11px;
    gap: 7px;
    font-size: 11px;
  }

  @media (max-width: 1280px) {
    font-size: 10px;
    gap: 5px;
    font-size: 10px;
  }
`;

// Column 2: Contact Info (Name, Email, Phone)
const ContactInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 14px;

  @media (max-width: 1680px) {
    font-size: 13px;
    gap: 10px;
    padding: 0;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    gap: 8px;
    padding: 0;
  }

  @media (max-width: 1440px) {
    font-size: 11px;
    gap: 6px;
    padding: 0;
  }

  @media (max-width: 1280px) {
    gap: 4px;
    padding: 0;
  }
`;

const CustomerName = styled.div`
  font-weight: 700;
  color: #1a202c;
  word-wrap: break-word;
  overflow-wrap: break-word;
  font-size: 14px;
  margin-bottom: 4px;

  @media(max-width: 1680px) {
    font-size: 13px;
    margin-bottom: 4px;
  }

  @media(max-width: 1600px) {
    font-size: 12px;
    margin-bottom: 2px;
  }

  @media(max-width: 1440px) {
    font-size: 11px;
  }

  @media(max-width: 1280px) {
    font-size: 10px;
    margin-bottom: 0px;
  }
`;

const ContactDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #374151;
  font-size: 13px;
  word-wrap: break-word;
  margin-bottom: 2px;
  
  svg {
    flex-shrink: 0;
    font-size: 12px;
    color: #9ca3af;
  }

  .value {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 55%;
  }

  @media(max-width: 1680px) {
    font-size: 13px;
    margin-bottom: 2px;
  }

  @media(max-width: 1600px) {
    font-size: 12px;
    margin-bottom: 0px;
  }

  @media(max-width: 1440px) {
    font-size: 11px;
  }

  @media(max-width: 1280px) {
    font-size: 10px;
  }
`;

const MultipleIndicator = styled.span`
  background: #e5e7eb;
  color: #6b7280;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
`;

const AddContactButton = styled.button`
  background: linear-gradient(135deg, #e0f2fe, #f0f9ff);
  border: 1px dashed #38bdf8;
  color: #0369a1;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  width: max-content;
  font-weight: 600;
  cursor: pointer;
  margin-top: 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  
  &:hover {
    background: linear-gradient(135deg, #bae6fd, #e0f2fe);
    color: #0c4a6e;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(56, 189, 248, 0.2);
    border-color: #0ea5e9;
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Column 3: Travel Details (Tour, Pax, Date, Message)
const TravelDetailsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 14px;
  padding: 0 15px;
`;

const TourNameEditable = styled.input`
  font-weight: 600;
  font-size: 16px;
  color: #1a202c;
  border: none;
  background: transparent;
  width: 100%;
  margin-bottom: 6px;
`;

const TravelDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  color: #374151;
  font-size: 16px;
  padding: 6px 0;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    color: #1f2937;
    transform: translateX(2px);
  }
  
  svg {
    font-size: 14px;
  }

  @media(max-width: 1680px) {
    font-size: 13px;
    gap: 10px;
    padding: 6px 0;
  }

  @media(max-width: 1600px) {
    font-size: 12px;
    gap: 8px;
    padding: 4px 0;
  }

  @media(max-width: 1440px) {
    font-size: 11px;
    gap: 6px;
    padding: 2px 0;
  }

  @media(max-width: 1280px) {
    font-size: 10px;
    gap: 4px;
    padding: 0px 0;
  }
`;

const EditableField = styled.div`
  border: none;
  background: transparent;
  color: #374151;
  font-size: 16px;
  font-weight: 600;
  width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media(max-width: 1680px) {
    font-size: 13px;
    width: 80%;
    margin-bottom: 4px;
  }

  @media(max-width: 1600px) {
    font-size: 12px;
    width: 80%;
    margin-bottom: 2px;
  }

  @media(max-width: 1440px) {
    font-size: 11px;
  }

  @media(max-width: 1280px) {
    font-size: 10px;
    width: 75%;
  }
`;

const MessagePreview = styled.div`
  font-size: 14px;
  color: #374151;
  width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media(max-width: 1680px) {
    font-size: 13px;
    width: 75%;
  }

  @media(max-width: 1600px) {
    font-size: 12px;
  }

  @media(max-width: 1440px) {
    font-size: 11px;
  }

  @media(max-width: 1280px) {
    font-size: 10px;
  }
`;

// Column 4: Owner Assignment
const OwnerColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 160px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  padding-left: 15px;

  @media(max-width: 1680px) {
    font-size: 13px;
  }

  @media(max-width: 1600px) {
    font-size: 12px;
  }

  @media(max-width: 1280px) {
    font-size: 10px;
  }
`;

const OwnerInfo = styled.div`
  padding: 10px 14px;
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  border: 1px solid #bae6fd;
  border-radius: 14px;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
  width: 75%;

  @media(max-width: 1680px) {
    font-size: 13px;
    padding: 10px 16px;
    gap: 8px;
    width: 70%;
  }

  @media(max-width: 1600px) {
    font-size: 12px;
    padding: 8px 12px;
    gap: 6px;
    width: 70%;
  }
  
`;

const OwnerName = styled.div`
  font-weight: 600;
  color: #1e40af;
  margin-bottom: 4px;
  font-size: 15px;

  @media(max-width: 1680px) {
    font-size: 13px;
  }

  @media(max-width: 1600px) {
    font-size: 12px;
  }

  @media(max-width: 1440px) {
    font-size: 11px;
  }

  @media(max-width: 1280px) {
    font-size: 10px;
  }
`;

const OwnerEmployeeId = styled.div`
  font-size: 13px;
  color: #3b82f6;
  font-weight: 500;

  @media(max-width: 1680px) {
    font-size: 10px;
  }

  @media(max-width: 1600px) {
    font-size: 9px;
  }
`;

const AssignButton = styled.button`
  padding: 10px 24px;
  background: linear-gradient(135deg, #48bb78, #38a169);
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s ease;
  width: 75%;
  
  &:hover {
    background: linear-gradient(135deg, #38a169, #2f855a);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(72, 187, 120, 0.3);
  }

  @media(max-width: 1680px) {
    font-size: 13px;
    padding: 10px 16px;
    gap: 8px;
    width: 75%;
  }

  @media(max-width: 1600px) {
    font-size: 12px;
    padding: 8px 12px;
    gap: 6px;
    width: 70%;
  }

  @media(max-width: 1280px) {
    font-size: 10px;
    padding: 6px 8px;
    width: 80%;
  }

  @media(max-width: 1440px) {
    font-size: 12px;
    padding: 6px 6px;
    gap: 4px;
    width: 60%;
  }
`;

const ReassignButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s ease;
  width: 75%;
  
  &:hover {
    background: linear-gradient(135deg, #d97706, #b45309);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  }

  @media(max-width: 1680px) {
    font-size: 13px;
    padding: 10px 16px;
    gap: 8px;
    width: 75%;
  }

  @media(max-width: 1600px) {
    font-size: 12px;
    padding: 8px 12px;
    gap: 6px;
  }
  
  @media(max-width: 1440px) {
    font-size: 12px;
    padding: 6px 6px;
    gap: 4px;
    width: 80%;
  }
  
  @media(max-width: 1280px) {
    font-size: 10px;
    padding: 4px 4px;
    gap: 2px;
    width: 60%;
  }
`;

const UnassignedBadge = styled.div`
  padding: 14px 18px;
  background: linear-gradient(135deg, #fef3cd, #fde68a);
  border: 1px solid #fbbf24;
  border-radius: 14px;
  font-size: 14px;
  color: #92400e;
  font-weight: 600;
  text-align: center;
  box-shadow: 0 2px 4px rgba(251, 191, 36, 0.1);
`;

// Column 5: Actions
const ActionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media(max-width: 1680px) {
    font-size: 13px;
    gap: 8px;
  }

  @media(max-width: 1600px) {
    font-size: 12px;
    gap: 6px;
  }

  @media(max-width: 1440px) {
    font-size: 12px;
    gap: 4px;
  }

  @media(max-width: 1280px) {
    font-size: 10px;
    gap: 2px;
  }
`;

const StatusDropdown = styled.select`
  padding: 8px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  width: 100%;
  margin-bottom: 4px;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
  }

  @media(max-width: 1680px) {
    font-size: 13px;
    padding: 6px 10px;
  }

  @media(max-width: 1600px) {
    font-size: 12px;
    padding: 4px 8px;
  }

  @media(max-width: 1440px) {
    font-size: 12px;
    padding: 4px 6px;
  }

  @media(max-width: 1280px) {
    font-size: 10px;
    padding: 2px 4px;
  }
`;

const ActionButton = styled.button`
  width: 100%;
  background: ${props => {
    if (props.type === 'comment') return 'linear-gradient(135deg, #48bb78, #38a169)';
    if (props.type === 'info') return 'linear-gradient(135deg, #8b5cf6, #7c3aed)';
    if (props.type === 'delete') return 'linear-gradient(135deg, #f56565, #e53e3e)';
    return 'linear-gradient(135deg, #4299e1, #3182ce)';
  }};
  color: white;
  border: none;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 12px 20px;
  font-size: 14px;
  gap: 6px;
  border-radius: 6px;
  margin-bottom: 4px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => {
    if (props.type === 'comment') return '0 6px 20px rgba(72, 187, 120, 0.4)';
    if (props.type === 'info') return '0 6px 20px rgba(139, 92, 246, 0.4)';
    if (props.type === 'delete') return '0 6px 20px rgba(245, 101, 101, 0.4)';
    return '0 6px 20px rgba(66, 153, 225, 0.4)';
  }};
  }
  
  &:active {
    transform: translateY(0);
  }

  @media(max-width: 1680px) {
    font-size: 13px;
    padding: 10px 16px;
    gap: 8px;
  }

  @media(max-width: 1600px) {
    font-size: 12px;
    padding: 8px 12px;
    gap: 6px;
  }

  @media(max-width: 1440px) {
    font-size: 12px;
    padding: 6px 6px;
    gap: 4px;
  }

  @media(max-width: 1280px) {
    font-size: 10px;
    padding: 4px 4px;
    gap: 2px;
  }
`;

// Column 6: View Button
const ViewColumn = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ViewButton = styled.button`
  padding: 8px 20px;
  background: linear-gradient(135deg, #4299e1, #3182ce);
  color: white;
  width: 100%;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  
  &:hover {
    background: linear-gradient(135deg, #3182ce, #2c5aa0);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(66, 153, 225, 0.3);
  }

  @media(max-width: 1680px) {
    font-size: 13px;
    padding: 8px 16px;
    gap: 8px;
  }

  @media(max-width: 1600px) {
    font-size: 12px;
    padding: 8px 12px;
    gap: 6px;
  }

  @media(max-width: 1440px) {
    font-size: 12px;
    padding: 6px 6px;
    gap: 4px;
  }

  @media(max-width: 1280px) {
    font-size: 10px;
    padding: 4px 4px;
    gap: 2px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const CardCheckbox = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
`;

const LeadMainInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.75rem, 1vw, 0.875rem);
  margin-bottom: clamp(1.25rem, 1.5vw, 1.25rem);
`;



const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.5rem, 1vw, 0.875rem);
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(0.5rem, 1vw, 0.875rem);
  font-size: clamp(0.8125rem, 1vw, 0.875rem);
`;

const ContactIcon = styled.div`
  color: #4299e1;
  width: clamp(1.125rem, 1.5vw, 1.25rem);
  font-size: clamp(1rem, 1.25vw, 1.125rem);
  text-shadow: 0 1px 2px rgba(66, 153, 225, 0.3);
`;

const MultipleEntries = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.1875rem, 0.375vw, 0.3125rem);
`;

const TravelInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.5rem, 1vw, 0.875rem);
`;

const TravelItem = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(0.5rem, 1vw, 0.875rem);
  font-size: clamp(0.8125rem, 1vw, 0.875rem);
`;

const TravelIcon = styled.div`
  color: #4299e1;
  width: clamp(1.125rem, 1.5vw, 1.25rem);
  font-size: clamp(1rem, 1.25vw, 1.125rem);
  text-shadow: 0 1px 2px rgba(66, 153, 225, 0.3);
`;

const OwnerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.5rem, 1vw, 0.875rem);
  align-items: flex-start;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(0.625rem, 1vw, 0.875rem);
  background: linear-gradient(135deg, #e6fffa, #f0fff4);
  padding: clamp(0.75rem, 1vw, 0.875rem) clamp(1rem, 1.5vw, 1.25rem);
  border-radius: clamp(0.75rem, 1vw, 0.875rem);
  border: 2px solid #68d391;
  color: #22543d;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(104, 211, 145, 0.2);
`;

const UserAvatar = styled.div`
  font-size: clamp(1rem, 1.25vw, 1.125rem);
`;

const ActionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.5rem, 1vw, 0.875rem);
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: clamp(0.375rem, 0.75vw, 0.625rem);
  background: ${props => props.color || '#667eea'};
  color: white;
  padding: clamp(0.5rem, 1vw, 0.875rem) clamp(0.875rem, 1.5vw, 1.25rem);
  border-radius: clamp(1.25rem, 1.5vw, 1.25rem);
  font-size: clamp(0.8125rem, 1vw, 0.875rem);
  font-weight: 600;
  margin-bottom: clamp(0.5rem, 1vw, 0.875rem);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-transform: uppercase;
  letter-spacing: clamp(0.03125rem, 0.0625vw, 0.0625rem);

  @media (max-width: 1680px) {
    font-size: 13px;
    padding: 6px 6px;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    padding: 4px 4px;
  }

  @media (max-width: 1440px) {
    font-size: 11px;
    padding: 4px 4px;
    gap: 4px;
  }

  @media (max-width: 1280px) {
    font-size: 10px;
    padding: 2px 2px;
    gap: 2px;
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  margin: 0;
  cursor: pointer;
  accent-color: #4299e1;
  transform: scale(1.1);
  
  &:hover {
    transform: scale(1.2);
  }
`;

const NotificationBadge = styled.div`
  background: #f56565;
  color: white;
  border-radius: 50%;
  width: clamp(1.125rem, 1.25vw, 1.25rem);
  height: clamp(1.125rem, 1.25vw, 1.25rem);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(0.625rem, 0.75vw, 0.75rem);
  font-weight: bold;
  position: relative;
  top: clamp(-0.125rem, -0.125vw, -0.125rem);
`;

// Modal Components
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
  overflow-x: hidden;
  backdrop-filter: blur(3px);
  margin: 0;

  @media (max-width: 1680px) {
    padding: 15px;
  }

  @media (max-width: 1600px) {
    padding: 12px;
  }

  @media (max-width: 1440px) {
    padding: 10px;
  }
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 0;
  padding: 30px;
  width: 90%;
  max-width: 450px;
  overflow: hidden;
  color: #000000;
  border: 5px solid #e8f2fd;
  border-radius: 8px;
  
  position: relative;
  z-index: 10000;
  
  /* Ensure perfect centering in viewport */
  margin: auto;
  
  /* Handle overflow */
  max-height: 80vh;
  overflow-y: auto;
  
  /* Ensure the modal content is always visible */
  display: block;

  @media (max-width: 1680px) {
    padding: 15px;
    max-width: 400px;
  }

  @media (max-width: 1600px) {
    padding: 12px;
    max-width: 350px;
  }

  @media (max-width: 1440px) {
    padding: 10px;
    max-width: 300px;
  }

  @media (max-width: 1280px) {
    padding: 8px;
  }
`;

const ModalHeader = styled.h3`
  margin: 0 0 24px 0;
  font-size: 22px;
  font-weight: 700;
  color: #1a202c;
  letter-spacing: -0.5px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e2e8f0;
  
  /* Ensure high visibility */
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  @media (max-width: 1680px) {
    font-size: 18px;
    padding-bottom: 8px;
  }

  @media (max-width: 1600px) {
    font-size: 16px;
    padding-bottom: 6px;
  }

  @media (max-width: 1440px) {
    font-size: 15px;
    padding-bottom: 4px;
  }

  @media (max-width: 1280px) {
    font-size: 13px;
    padding-bottom: 2px;
  }
`;

const ModalSelect = styled.select`
  width: 100%;
  padding: 16px 20px;
  margin: 16px 0;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  color: #1a202c;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 18px;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 4px rgba(66, 153, 225, 0.1);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }
  
  option {
    padding: 12px;
    color: #1a202c;
    background: white;
    
    &:checked {
      background: #4299e1;
      color: white;
    }
  }

  @media (max-width: 1680px) {
    font-size: 13px;
    padding: 10px 16px;
    margin: 10px 0;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    padding: 8px 12px;
    margin: 8px 0;
  }

  @media (max-width: 1440px) {
    font-size: 11px;
    padding: 6px 6px;
    margin: 6px 0;
  }

  @media (max-width: 1280px) {
    font-size: 10px;
    padding: 4px 4px;
    margin: 4px 0;
  }
`;

const Label = styled.label`
  font-size: 15px;
  font-weight: 500;
  color: #1a202c;
  margin-bottom: 3px;

  @media (max-width: 1680px) {
    font-size: 13px;
    margin-bottom: 4px;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    margin-bottom: 2px;
  }

  @media (max-width: 1440px) {
    font-size: 11px;
    margin-bottom: 1px;
  }

  @media (max-width: 1280px) {
    font-size: 9px;
    margin-bottom: 1px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 20px;
  margin: 16px 0;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  color: #1a202c;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 4px rgba(66, 153, 225, 0.1);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }
  
  &::placeholder {
    color: #a0aec0;
    font-weight: 400;
  }

  @media (max-width: 1680px) {
    font-size: 13px;
    padding: 10px 16px;
    margin: 10px 0;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    padding: 8px 12px;
    margin: 8px 0;
  }

  @media (max-width: 1440px) {
    font-size: 11px;
    padding: 6px 6px;
    margin: 6px 0;
  }

  @media (max-width: 1280px) {
    font-size: 10px;
    padding: 4px 4px;
    margin: 4px 0;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 16px 20px;
  margin: 16px 0;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  color: #1a202c;
  min-height: 120px;
  resize: vertical;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.2s ease;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 4px rgba(66, 153, 225, 0.1);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }
  
  &::placeholder {
    color: #a0aec0;
    font-weight: 400;
  }

  @media (max-width: 1680px) {
    font-size: 13px;
    padding: 10px 16px;
    margin: 10px 0;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    padding: 8px 12px;
    margin: 8px 0;
  }

  @media (max-width: 1440px) {
    font-size: 11px;
    padding: 6px 6px;
    margin: 6px 0;
  }

  @media (max-width: 1280px) {
    font-size: 10px;
    padding: 4px 4px;
    margin: 4px 0;
  }
`;

const ModalInfo = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  color: #4a5568;
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
  
  strong {
    color: #1a202c;
    font-weight: 600;
  }

  @media (max-width: 1680px) {
    font-size: 13px;
    padding: 10px 16px;
    margin: 10px 0;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    padding: 8px 12px;
    margin: 8px 0;
  }

  @media (max-width: 1440px) {
    font-size: 11px;
    padding: 6px 6px;
    margin: 6px 0;
  }

  @media (max-width: 1280px) {
    font-size: 10px;
    padding: 4px 4px;
    margin: 4px 0;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 2px solid #e2e8f0;

  @media (max-width: 1680px) {
    font-size: 13px;
    padding: 10px 16px;
    margin: 10px 0;
  }

  @media (max-width: 1600px) {
    font-size: 12px;
    padding: 8px 12px;
    margin: 8px 0;
  }

  @media (max-width: 1440px) {
    font-size: 11px;
    padding: 6px 6px;
    margin: 6px 0;
  }

  @media (max-width: 1280px) {
    font-size: 10px;
    padding: 4px 4px;
    margin: 4px 0;
  }
`;

const Button = styled.button`
  padding: 12px 24px !important;
  border: 2px solid #4299e1 !important;
  border-radius: 8px !important;
  cursor: pointer !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  
  ${props => props.primary ? `
    background: linear-gradient(135deg, #4299e1, #3182ce) !important;
    color: white !important;
    
    &:hover {
      background: linear-gradient(135deg, #3182ce, #2c5aa0) !important;
    }
  ` : `
    background: white !important;
    color: #4299e1 !important;
    
    &:hover {
      background: #f1f5f9 !important;
    }
  `}

  @media (max-width: 1680px) {
    font-size: 13px !important;
    padding: 10px 16px !important;
    margin: 10px 0 !important;
  }

  @media (max-width: 1600px) {
    font-size: 12px !important;
    padding: 8px 12px !important;
    margin: 8px 0 !important;
  }

  @media (max-width: 1440px) {
    font-size: 11px !important;
    padding: 6px 6px !important;
    margin: 6px 0 !important;
  }

  @media (max-width: 1280px) {
    font-size: 10px !important;
    padding: 4px 4px !important;
    margin: 4px 0 !important;
  }
`;

const TopRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const RightAddButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4299e1, #3182ce);
  color: white;
  cursor: pointer;
  border: none;
  outline: none;
  font-size: 16px;
  font-weight: 600;

  @media (max-width: 1680px) {
    font-size: 16px;
    padding: 10px 16px;
    gap: 8px;
  }

  @media (max-width: 1600px) {
    font-size: 14px;
    padding: 8px 12px;
    gap: 6px;
  }

  @media (max-width: 1440px) {
    font-size: 12px;
    padding: 6px 10px;
    gap: 4px;
  }
`;

const RefreshButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-radius: 8px;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  cursor: pointer;
  border: none;
  outline: none;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #059669, #047857);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 1680px) {
    font-size: 16px;
    padding: 10px 16px;
    gap: 8px;
  }

  @media (max-width: 1600px) {
    font-size: 14px;
    padding: 8px 12px;
    gap: 6px;
  }

  @media (max-width: 1440px) {
    font-size: 12px;
    padding: 6px 10px;
    gap: 4px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  cursor: pointer;
  background: #4299e1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  top: 20px;
  right: 30px;
  border: none;
  color: white;
  font-size: 20px;
  font-weight: 600;
  padding: 10px;
  border-radius: 50%;
  width: 40px;
  height: 40px;

  @media (max-width: 1680px) {
    font-size: 16px;
    padding: 10px;
    width: 30px;
    height: 30px;
    top: 10px;
    right: 20px;
  }

  @media (max-width: 1600px) {
    font-size: 14px;
    padding: 8px;
    width: 25px;
    height: 25px;
    top: 8px;
    right: 5px;
  }

  @media (max-width: 1440px) {
    font-size: 12px;
    padding: 6px;
    width: 20px;
    height: 20px;
    top: 5px;
    right: 4px;
  }

  @media (max-width: 1280px) {
    font-size: 10px;
    padding: 4px;
    width: 15px;
    height: 15px;
    top: 8px;
    right: 4px;
  }
`;

const LeadManagement = ({ currentUser }) => {
  const [leads, setLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: 'all_time',
    agent: 'all',
    status: 'all',
    source: 'all',
    search: ''
  });

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [modalData, setModalData] = useState({});
  const [newLeads, setNewLeads] = useState(0);
  const [showDetailView, setShowDetailView] = useState(false);
  const [detailViewLead, setDetailViewLead] = useState(null);
  const [notification, setNotification] = useState(null);
  const [leadsToShow, setLeadsToShow] = useState(10);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);

  // Fetch leads from API
  const fetchLeads = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const apiFilters = {};
      if (filters.dateRange !== 'all_time') apiFilters.dateRange = filters.dateRange;
      if (filters.agent !== 'all') apiFilters.agent = filters.agent;
      if (filters.status !== 'all') apiFilters.status = filters.status;
      if (filters.source !== 'all') apiFilters.source = filters.source;
      // Remove search from backend - we'll do client-side search
      // if (filters.search) apiFilters.search = filters.search;
      // Fetch all leads since we're doing client-side filtering
      apiFilters.page = 1;
      apiFilters.limit = 1000; // Large limit to get all leads

      const response = await apiService.getAllLeads(apiFilters);
      setLeads(response.leads || []);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      setError(error.message || 'Failed to fetch leads');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      const response = await apiService.getAllUsers();
      setUsers(response || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  // Refresh leads function
  const handleRefreshLeads = async () => {
    try {
      setIsRefreshing(true);
      await fetchLeads();
      await fetchUsers();
      showNotification('Leads refreshed successfully', 'success');
    } catch (error) {
      console.error('Failed to refresh leads:', error);
      showNotification('Failed to refresh leads', 'error');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Fetch leads on component mount and when filters change
  useEffect(() => {
    fetchLeads();
    fetchUsers();
  }, [filters]);

  useEffect(() => {
    // Count new leads for notification
    const newLeadsCount = leads.filter(lead => lead?.status === 'new').length;
    setNewLeads(newLeadsCount);
  }, [leads]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const resetFilters = () => {
    setFilters({
      dateRange: 'all_time',
      agent: 'all',
      status: 'all',
      source: 'all',
      search: ''
    });
    setLeadsToShow(10); // Reset pagination
    showNotification('Filters reset to default', 'info');
  };

  const handleLoadMore = () => {
    setIsLoadingMore(true);
    // Simulate loading delay
    setTimeout(() => {
      setLeadsToShow(prev => prev + 10);
      setIsLoadingMore(false);
    }, 500);
  };

  const handleCheckboxChange = (leadId) => {
    setSelectedLeads(prev =>
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    const currentPageLeads = filteredLeads.slice(0, leadsToShow);
    if (selectedLeads.length === currentPageLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(currentPageLeads.map(lead => lead.id));
    }
  };

  const handleBulkAssign = () => {
    if (selectedLeads.length === 0) return;
    setModalType('bulk_assign');
    setShowModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedLeads.length === 0) return;
    setModalType('bulk_delete');
    setShowModal(true);
  };

  const handleDeselectAll = () => {
    setSelectedLeads([]);
    showNotification('All leads deselected', 'info');
  };

  const handleAssignLead = async (leadId, ownerId) => {
    try {
      // Convert empty string to null for unassigning
      const finalOwnerId = ownerId === '' ? null : ownerId;
      await apiService.assignLead(leadId, finalOwnerId);
      await fetchLeads(); // Refresh the leads list

      if (finalOwnerId === null) {
        showNotification('Lead unassigned successfully');
      } else {
        showNotification('Lead assigned successfully');
      }
    } catch (error) {
      console.error('Failed to assign lead:', error);
      showNotification(error.message || 'Failed to assign lead', 'error');
    }
  };

  const handleStatusUpdate = async (leadId, newStatus) => {
    try {
      await apiService.updateLeadStatus(leadId, newStatus);
      await fetchLeads(); // Refresh the leads list
      showNotification('Lead status updated successfully');
    } catch (error) {
      console.error('Failed to update lead status:', error);
      showNotification(error.message || 'Failed to update lead status', 'error');
    }
  };

  const handleAddComment = async (leadId, comment) => {
    try {
      await apiService.addLeadComment(leadId, comment);
      await fetchLeads(); // Refresh the leads list
      showNotification('Comment added successfully');
    } catch (error) {
      console.error('Failed to add comment:', error);
      showNotification(error.message || 'Failed to add comment', 'error');
    }
  };

  const handleAddAdditionalInfo = (leadId, type, value) => {
    setLeads(prev => prev.map(lead => {
      if (lead.id === leadId) {
        const updatedLead = { ...lead };

        if (type === 'email') {
          updatedLead.emails = [
            ...lead.emails,
            {
              value,
              isPrimary: false,
              addedBy: currentUser?.id,
              addedAt: new Date().toISOString()
            }
          ];
        } else if (type === 'phone') {
          updatedLead.phones = [
            ...lead.phones,
            {
              value,
              isPrimary: false,
              addedBy: currentUser?.id,
              addedAt: new Date().toISOString()
            }
          ];
        }

        updatedLead.timeline = [
          ...lead.timeline,
          {
            id: lead.timeline.length + 1,
            action: 'Additional Info Added',
            description: `Added ${type}: ${value}`,
            userId: currentUser?.id,
            userName: currentUser?.fullName,
            timestamp: new Date().toISOString()
          }
        ];

        return updatedLead;
      }
      return lead;
    }));
  };

  const handleDeleteLead = async (leadId) => {
    try {
      await apiService.deleteLead(leadId);
      await fetchLeads(); // Refresh the leads list
      showNotification('Lead deleted successfully');
    } catch (error) {
      console.error('Failed to delete lead:', error);
      showNotification(error.message || 'Failed to delete lead', 'error');
    }
  };

  const openModal = (type, lead = null) => {
    if (type === 'view') {
      setDetailViewLead(lead);
      setShowDetailView(true);
    } else {
      setModalType(type);
      setSelectedLead(lead);
      setModalData({});
      setShowModal(true);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setSelectedLead(null);
    setModalData({});
  };

  const openDetailView = async (lead) => {
    try {
      // Fetch the full lead data including timeline and comments
      const detailedLead = await apiService.getLead(lead.id);
      setDetailViewLead(detailedLead);
      setShowDetailView(true);
    } catch (error) {
      console.error('Failed to fetch lead details:', error);
      showNotification('Failed to load lead details', 'error');
    }
  };

  const closeDetailView = () => {
    setShowDetailView(false);
    setDetailViewLead(null);
  };

  const handleLeadUpdate = async (updatedLead) => {
    try {
      // Check if this is a backend lead data (has customer_name) or frontend data (has name)
      if (updatedLead.customer_name) {
        // This is backend data, just refresh the leads list
        await fetchLeads();

        // If detail view is open, refresh the detail view data
        if (showDetailView && detailViewLead && detailViewLead.id === updatedLead.id) {
          try {
            const refreshedLead = await apiService.getLead(updatedLead.id);
            setDetailViewLead(refreshedLead);
          } catch (error) {
            console.error('Failed to refresh detail view:', error);
          }
        }
      } else {
        // This is frontend data, transform and update
        const updateData = {};

        // Only include fields that are actually being updated
        if (updatedLead.name !== undefined) updateData.customer_name = updatedLead.name;
        if (updatedLead.tourName !== undefined) updateData.tour_name = updatedLead.tourName;
        if (updatedLead.numberOfPax !== undefined) updateData.number_of_passengers = updatedLead.numberOfPax;
        if (updatedLead.travelDate !== undefined) updateData.travel_date = updatedLead.travelDate;
        if (updatedLead.message !== undefined) updateData.message = updatedLead.message;
        if (updatedLead.status !== undefined) updateData.status = updatedLead.status;
        if (updatedLead.ownerId !== undefined) updateData.owner_id = updatedLead.ownerId;
        if (updatedLead.source !== undefined) updateData.source = updatedLead.source;

        await apiService.updateLead(updatedLead.id, updateData);

        // Refresh both the leads list and the detail view data
        await fetchLeads();

        // If detail view is open, refresh the detail view data
        if (showDetailView && detailViewLead && detailViewLead.id === updatedLead.id) {
          try {
            const refreshedLead = await apiService.getLead(updatedLead.id);
            setDetailViewLead(refreshedLead);
          } catch (error) {
            console.error('Failed to refresh detail view:', error);
          }
        }
      }

      showNotification('Lead updated successfully');
    } catch (error) {
      console.error('Failed to update lead:', error);
      showNotification(error.message || 'Failed to update lead', 'error');
    }
  };

  const handleModalSubmit = async () => {
    switch (modalType) {
      case 'add_lead':
        if (modalData.name && modalData.email && modalData.phone) {
          try {
            const leadData = {
              source: 'manual',
              customer_name: modalData.name,
              tour_name: modalData.tourName || '',
              number_of_passengers: modalData.numberOfPax ? parseInt(modalData.numberOfPax) : 1,
              travel_date: modalData.travelDate || '',
              message: modalData.message || '',
              emails: [{
                value: modalData.email,
                isPrimary: true
              }],
              phones: [{
                value: modalData.phone,
                isPrimary: true
              }]
            };

            await apiService.createLead(leadData);
            await fetchLeads(); // Refresh the leads list
            showNotification('New lead added successfully!', 'success');
            closeModal();
          } catch (error) {
            console.error('Failed to create lead:', error);
            showNotification(error.message || 'Failed to create lead', 'error');
          }
        }
        break;
      case 'comment':
        if (modalData.comment && selectedLead) {
          handleAddComment(selectedLead.id, modalData.comment);
        }
        break;
      case 'additional_info':
        if (modalData.type && modalData.value && selectedLead) {
          handleAddAdditionalInfo(selectedLead.id, modalData.type, modalData.value);
        }
        break;
      case 'bulk_assign':
        if (modalData.ownerId !== undefined) {
          try {
            const count = selectedLeads.length;
            const finalOwnerId = modalData.ownerId === '' ? null : modalData.ownerId;

            // Bulk assign leads
            for (const leadId of selectedLeads) {
              await apiService.assignLead(leadId, finalOwnerId);
            }

            await fetchLeads(); // Refresh the leads list

            // Show bulk notification
            if (finalOwnerId) {
              showNotification(`${count} leads assigned successfully`, 'success');
            } else {
              showNotification(`${count} leads unassigned`, 'info');
            }

            setSelectedLeads([]);
            closeModal();
          } catch (error) {
            console.error('Failed to bulk assign leads:', error);
            showNotification(error.message || 'Failed to bulk assign leads', 'error');
          }
        }
        break;

        setSelectedLeads([]);
        break;
      case 'bulk_delete':
        if (selectedLeads.length > 0) {
          try {
            await apiService.bulkDeleteLeads(selectedLeads);
            await fetchLeads(); // Refresh the leads list
            showNotification(`${selectedLeads.length} lead${selectedLeads.length > 1 ? 's' : ''} deleted successfully`);
            setSelectedLeads([]);
          } catch (error) {
            console.error('Failed to bulk delete leads:', error);
            showNotification(error.message || 'Failed to delete leads', 'error');
          }
        }
        break;
      case 'assign':
        if (selectedLead && modalData.ownerId) {
          handleAssignLead(selectedLead.id, modalData.ownerId);
        }
        break;
      case 'reassign':
        if (selectedLead && (modalData.ownerId !== undefined)) {
          handleAssignLead(selectedLead.id, modalData.ownerId);
        }
        break;
      default:
        break;
    }
    closeModal();
  };

  // Filter leads based on current filters and user role
  const filteredLeads = leads.filter(lead => {
    let matches = true;

    // Role-based visibility filter (applied first)
    if (!currentUser?.isAdmin) {
      // Regular users only see leads assigned to them
      matches = matches && (lead.owner_id === currentUser?.id);
    }
    // Admins see all leads (assigned and unassigned)

    // Date filter
    if (filters.dateRange !== 'all_time') {
      const today = new Date();
      const leadDate = new Date(lead.created_at);

      switch (filters.dateRange) {
        case 'today':
          matches = matches && leadDate.toDateString() === today.toDateString();
          break;
        case 'yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(today.getDate() - 1);
          matches = matches && leadDate.toDateString() === yesterday.toDateString();
          break;
        case 'this_week':
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay());
          matches = matches && leadDate >= weekStart;
          break;
        case 'last_week':
          const lastWeekStart = new Date(today);
          lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
          const lastWeekEnd = new Date(lastWeekStart);
          lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
          matches = matches && leadDate >= lastWeekStart && leadDate <= lastWeekEnd;
          break;
        case 'this_month':
          const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
          const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          matches = matches && leadDate >= thisMonthStart && leadDate <= thisMonthEnd;
          break;
        case 'last_month':
          const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
          matches = matches && leadDate >= lastMonthStart && leadDate <= lastMonthEnd;
          break;
        case 'this_year':
          matches = matches && leadDate.getFullYear() === today.getFullYear();
          break;
        case 'last_year':
          matches = matches && leadDate.getFullYear() === today.getFullYear() - 1;
          break;
        case 'select_date':
          const selectedDate = new Date(filters.selectDate);
          matches = matches && leadDate.toDateString() === selectedDate.toDateString();
          break;
        default:
          break;
      }
    }

    // Agent filter
    if (filters.agent !== 'all') {
      if (filters.agent === 'unassigned') {
        matches = matches && !lead.owner_id;
      } else {
        matches = matches && lead.owner_id === parseInt(filters.agent);
      }
    }

    // Status filter
    if (filters.status !== 'all') {
      matches = matches && lead.status === filters.status;
    }

    // Source filter
    if (filters.source !== 'all') {
      matches = matches && lead.source === filters.source;
    }

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();

      // Debug: Log the first lead to see its structure
      if (lead.id === leads[0]?.id) {
        console.log('Lead structure:', lead);
        console.log('Emails:', lead.emails);
        console.log('Phones:', lead.phones);
      }

      // Enhanced searchable fields including email and phone
      const searchableFields = [
        lead.customer_name,
        lead.tour_name,
        lead.id,
        lead.message,
        // Email addresses - search individual emails
        ...(lead.emails?.map(email => email.contact_value) || []),
        // Phone numbers - search individual phones
        ...(lead.phones?.map(phone => phone.contact_value) || [])
      ].filter(Boolean).join(' ').toLowerCase();

      // Check if search term matches any of the searchable fields
      matches = matches && searchableFields.includes(searchTerm);
    }

    return matches;
  });

  // Apply pagination to filtered leads
  const paginatedLeads = filteredLeads.slice(0, leadsToShow);
  const hasMoreLeads = filteredLeads.length > leadsToShow;

  // Calculate statistics
  const stats = {
    total: leads.length,
    new: leads.filter(lead => lead.status === 'new').length,
    assigned: leads.filter(lead => lead.owner_id !== null).length,
    unassigned: leads.filter(lead => lead.owner_id === null).length
  };

  // Reset pagination when filters change
  useEffect(() => {
    setLeadsToShow(10);
  }, [filters.dateRange, filters.agent, filters.status, filters.source, filters.search]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (showModal) {
          closeModal();
        } else if (showDetailView) {
          closeDetailView();
        }
      }
    };

    if (showModal || showDetailView) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when modal or detail view is open
      document.body.style.overflow = 'hidden';
      // Ensure no parent containers can constrain the modal/detail view
      document.body.style.position = 'relative';
    } else {
      // Restore body scroll when modal and detail view are closed
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      // Always restore body scroll on cleanup
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
    };
  }, [showModal, showDetailView]);

  return (
    <>
      <Container>
        {/* <StatsRow>
          <StatCard>
            <h3>{stats.total}</h3>
            <p>Total Leads</p>
          </StatCard>
          <StatCard>
            <h3>{stats.new}</h3>
            <p>New Leads</p>
          </StatCard>
          <StatCard>
            <h3>{stats.assigned}</h3>
            <p>Assigned Leads</p>
          </StatCard>
          <StatCard>
            <h3>{stats.unassigned}</h3>
            <p>Unassigned Leads</p>
          </StatCard>
        </StatsRow> */}


        <Header>
          <TopRow>
            <Title>
              <FaUsers /> Lead Management
              {/* {newLeads > 0 && (
                <NotificationBadge>{newLeads}</NotificationBadge>
              )} */}
            </Title>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <RightAddButton onClick={() => openModal('add_lead')}>
                <FaPlus /> Add New Lead
              </RightAddButton>
              <RefreshButton
                onClick={handleRefreshLeads}
                disabled={isRefreshing}
                style={{
                  opacity: isRefreshing ? 0.7 : 1,
                  cursor: isRefreshing ? 'not-allowed' : 'pointer'
                }}
              >
                <FaSync style={{
                  animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                  transform: isRefreshing ? 'rotate(360deg)' : 'rotate(0deg)'
                }} />
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </RefreshButton>
            </div>
          </TopRow>
          <hr style={{ width: '100%', margin: '0.625rem 0', border: '1px solid #e2e8f0', background: '#e2e8f0' }} />
          <Grid>
            <GridColumnSearch>
              <FilterGroup>
                <FilterLabel>Search</FilterLabel>
                <SearchBox>
                  <SearchIcon />
                  <SearchInput
                    placeholder="Search leads, emails, phones..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  />
                </SearchBox>
              </FilterGroup>
            </GridColumnSearch>

            <FilterSection>
              <FilterGroup>
                <FilterLabel>Date Range</FilterLabel>
                <Select
                  value={filters.dateRange}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                >
                  <option value="all_time">All Time</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="this_week">This Week</option>
                  <option value="last_week">Last Week</option>
                  <option value="this_month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="this_year">This Year</option>
                  <option value="last_year">Last Year</option>
                  <option value="select_date">Select Date</option>
                </Select>
              </FilterGroup>

              {filters.dateRange === 'select_date' && (
                <FilterGroup>
                  <FilterLabel>Select Date</FilterLabel>
                  <InputSelectDate
                    type="date"
                    value={filters.selectDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, selectDate: e.target.value }))}
                  />
                </FilterGroup>
              )}

              {currentUser?.isAdmin ?
                <FilterGroup>
                  <FilterLabel>Assigned To</FilterLabel>
                  <Select
                    value={filters.agent}
                    onChange={(e) => setFilters(prev => ({ ...prev, agent: e.target.value }))}
                  >
                    <option value="all">All Users</option>
                    <option value="unassigned">Unassigned</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </Select>
                </FilterGroup>
                : <></>}

              <FilterGroup>
                <FilterLabel>Status</FilterLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="all">All Status</option>
                  {leadStatuses.map(status => (
                    <option key={status.id} value={status.id}>{status.name}</option>
                  ))}
                </Select>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Source</FilterLabel>
                <Select
                  value={filters.source}
                  onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value }))}
                >
                  <option value="all">All Sources</option>
                  {leadSources.map(source => (
                    <option key={source.id} value={source.id}>{source.name}</option>
                  ))}
                </Select>
              </FilterGroup>

              <FilterGroup>
                {/* <FilterLabel>&nbsp;</FilterLabel> */}
                <ResetButton onClick={resetFilters}>
                  <FaUndo />
                  Reset Filters
                </ResetButton>
              </FilterGroup>
            </FilterSection>
          </Grid>
        </Header>

        {currentUser?.isAdmin && selectedLeads.length > 0 ?
          <BulkActionsContainer>
            <BulkActionsCard style={{
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              border: '2px solid #38bdf8',
              boxShadow: '0 8px 32px rgba(56, 189, 248, 0.2)'
            }}>
              <BulkActionsTitle style={{ color: '#0369a1', fontSize: '1.125rem' }}>
                🎯 {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''} selected
              </BulkActionsTitle>
              <BulkActionsButtonGroup>
                <BulkButton onClick={handleBulkAssign} style={{
                  background: 'linear-gradient(135deg, #4299e1, #3182ce)',
                  boxShadow: '0 4px 16px rgba(66, 153, 225, 0.4)'
                }}>
                  <FaUser /> Assign to User
                </BulkButton>
                <BulkButton onClick={handleBulkDelete} style={{
                  background: 'linear-gradient(135deg, #f56565, #e53e3e)',
                  boxShadow: '0 4px 16px rgba(245, 101, 101, 0.4)'
                }}>
                  <FaTrash /> Delete Selected
                </BulkButton>
                <BulkButton onClick={handleDeselectAll} style={{
                  background: 'linear-gradient(135deg, #6b7280, #4b5563)',
                  boxShadow: '0 4px 16px rgba(107, 114, 128, 0.4)'
                }}>
                  <FaTimes /> Deselect All
                </BulkButton>
              </BulkActionsButtonGroup>
            </BulkActionsCard>
          </BulkActionsContainer>
          : <></>}

        {/* Header Controls */}
        {currentUser?.isAdmin ? (
          <div style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Checkbox
                type="checkbox"
                checked={selectedLeads.length === paginatedLeads.length && paginatedLeads.length > 0}
                onChange={handleSelectAll}
              />
              <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '500' }}>
                Select All ({paginatedLeads.length} leads)
              </span>
            </div> */}
          </div>
        ) : <></>}

        <div style={{ width: '100%', overflowX: 'auto', borderRadius: '1rem' }}>
          <LeadsTable>
            {/* Table Header */}
            <TableHeader isAdmin={currentUser?.isAdmin}>
              {currentUser?.isAdmin ?
                <TableHeaderCell>
                  {/* <Checkbox
                    type="checkbox"
                    checked={selectedLeads.length === paginatedLeads.length && paginatedLeads.length > 0}
                    onChange={handleSelectAll}
                  /> */}
                </TableHeaderCell>
                : <></>}
              <TableHeaderCell isAdmin={currentUser?.isAdmin}>
                <FaTag />
                Lead Info
              </TableHeaderCell>
              <TableHeaderCell>
                <FaUser />
                Contact Details
              </TableHeaderCell>
              <TableHeaderCell>
                <TitlesPadding>
                  <FaMapMarkerAlt />
                  Travel Details
                </TitlesPadding>
              </TableHeaderCell>
              <TableHeaderCell>
                <TitlesPadding owner={true}>
                  <FaUsers />
                  Owner
                </TitlesPadding>
              </TableHeaderCell>
              <TableHeaderCell>
                <FaCog />
                Actions
              </TableHeaderCell>
            </TableHeader>

            {/* Table Rows */}
            {paginatedLeads.map(lead => {
              const source = getLeadSourceById(lead.source);
              const status = getStatusById(lead.status);
              const owner = lead.owner_id ? {
                id: lead.owner_id,
                fullName: lead.owner_name || `User ${lead.owner_id}`,
                employeeId: lead.owner_employee_id || `EMP${lead.owner_id.toString().padStart(3, '0')}`,
                avatar: '👤'
              } : null;

              return (
                <TableRow key={lead.id} statusColor={status?.color} isAdmin={currentUser?.isAdmin}>
                  {/* Checkbox Cell */}
                  {currentUser?.isAdmin ?
                    <TableCell>
                      <Checkbox
                        type="checkbox"
                        checked={selectedLeads.includes(lead.id)}
                        onChange={() => handleCheckboxChange(lead.id)}
                      />
                    </TableCell>
                    : <></>}

                  {/* Column 1: Lead Info (ID, Source, Date, Time) */}
                  <TableCell isAdmin={currentUser?.isAdmin}>
                    <LeadInfoColumn>
                      <InfoGridBandage>
                        <LeadIdBadge>{lead.id}</LeadIdBadge>
                        {/* Duplicate Indicator */}
                        {lead.isDuplicate && (
                          <LeadDuplicateBadge>
                            🔄 Dupli
                          </LeadDuplicateBadge>
                        )}
                        {/* Status Display */}
                        <StatusBadgeSource color={status?.color}>
                          {status?.icon} {status?.name}
                        </StatusBadgeSource>
                        <SourceBadge color={source?.color}>
                          <span>{source?.icon}</span>
                          <span>{source?.name}</span>
                        </SourceBadge>
                      </InfoGridBandage>
                      <DateTimeInfo>
                        <div>📅 {new Date(lead.created_at).toLocaleDateString()}</div>
                        <div>🕐 {new Date(lead.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>
                      </DateTimeInfo>
                    </LeadInfoColumn>
                  </TableCell>

                  {/* Column 2: Contact Details (Name, Email, Phone) */}
                  <TableCell isAdmin={currentUser?.isAdmin}>
                    <ContactInfoColumn>
                      <CustomerName>{lead.customer_name}</CustomerName>

                      {/* Emails */}
                      {lead.emails && lead.emails.length > 0 ? (
                        lead.emails.map((email, index) => (
                          <ContactDetail key={index}>
                            {email.is_primary ? <span style={{ background: '#10b981', color: 'white', padding: '0.0625rem 0.1875rem', borderRadius: '0.125rem', fontSize: '0.5rem' }}>P</span> : <></>}
                            <FaEnvelope />
                            <span className='value'>{email.contact_value}</span>
                          </ContactDetail>
                        ))
                      ) : (
                        <ContactDetail>
                          <FaEnvelope />
                          <span style={{ color: '#999', fontStyle: 'italic' }}>No email</span>
                        </ContactDetail>
                      )}

                      {/* Phones */}
                      {lead.phones && lead.phones.length > 0 ? (
                        lead.phones.map((phone, index) => (
                          <ContactDetail key={index}>
                            {phone.is_primary ? <span style={{ background: '#10b981', color: 'white', padding: '0.0625rem 0.1875rem', borderRadius: '0.125rem', fontSize: '0.5rem' }}>P</span> : <></>}
                            <FaPhone />
                            <span className='value'>{phone.contact_value}</span>
                          </ContactDetail>
                        ))
                      ) : (
                        <ContactDetail>
                          <FaPhone />
                          <span style={{ color: '#999', fontStyle: 'italic' }}>No phone</span>
                        </ContactDetail>
                      )}

                      {/* Add Contact Button (for users with edit permission) */}
                      {/* {(owner?.id === currentUser?.id || currentUser?.role === 'admin') && (
                        <AddContactButton onClick={() => openModal('additional_info', lead)}>
                          + Add Contact
                        </AddContactButton>
                      )} */}
                    </ContactInfoColumn>
                  </TableCell>

                  {/* Column 3: Travel Details (Tour, Pax, Date, Message) */}
                  <TableCell isAdmin={currentUser?.isAdmin}>
                    <TravelDetailsColumn>
                      {/* <TourNameEditable
                        value={lead.tourName}
                        onChange={(e) => handleLeadUpdate({ ...lead, tourName: e.target.value })}
                        readOnly={!owner || (!currentUser?.isAdmin && owner?.id !== currentUser?.id)}
                      /> */}

                      <EditableField>
                        <b>{lead.tour_name}</b>
                      </EditableField>

                      <TravelDetail>
                        <FaUsers style={{ color: '#8b5cf6', fontSize: '1.125rem' }} />
                        <EditableField>
                          <span>{lead.number_of_passengers} people</span>
                        </EditableField>
                      </TravelDetail>

                      <TravelDetail>
                        <FaCalendarAlt style={{ color: '#10b981', fontSize: '0.6875rem' }} />
                        <EditableField>
                          <span>{lead.travel_date}</span>
                        </EditableField>
                      </TravelDetail>

                      {/* Message Preview */}
                      {lead.message && (
                        <MessagePreview title={lead.message}>
                          💬 "{lead.message}"
                        </MessagePreview>
                      )}
                    </TravelDetailsColumn>
                  </TableCell>

                  {/* Column 4: Owner Assignment */}
                  <TableCell isAdmin={currentUser?.isAdmin}>
                    <OwnerColumn>
                      {!owner ? (
                        // Unassigned lead - only admins can see and assign
                        currentUser?.isAdmin ? (
                          <AssignButton onClick={() => openModal('assign', lead)}>
                            <FaUser />
                            Assign
                          </AssignButton>
                        ) : (
                          <UnassignedBadge>
                            🚫 Unassigned
                          </UnassignedBadge>
                        )
                      ) : (
                        // Assigned lead - show owner info and reassignment option
                        <>
                          <OwnerInfo>
                            <OwnerName>{owner.fullName}</OwnerName>
                            <OwnerEmployeeId>ID: {owner.employeeId}</OwnerEmployeeId>
                          </OwnerInfo>
                          {currentUser?.isAdmin ?
                            <ReassignButton onClick={() => openModal('reassign', lead)}>
                              {/* reassign icon */}
                              <FaUser /> {" "} Reassign
                            </ReassignButton>
                            : <></>}
                        </>
                      )}
                    </OwnerColumn>
                  </TableCell>

                  {/* Column 5: Actions (Status, Comments, View) */}
                  <TableCell isAdmin={currentUser?.isAdmin}>
                    <ActionsColumn>
                      {/* Status Update */}
                      {(owner?.id === currentUser?.id || currentUser?.isAdmin) ? (
                        <StatusDropdown
                          value={lead?.status || 'closed'}
                          style={{ background: `${status?.color || '#f1f5f9'}20` }}
                          onChange={(e) => handleStatusUpdate(lead.id, e.target.value)}
                        >
                          {leadStatuses.map(status => (
                            <option key={status.id} value={status.id}>
                              {status.icon} {status.name}
                            </option>
                          ))}
                        </StatusDropdown>
                      ) : (
                        <div style={{
                          background: status?.color ? `${status.color}20` : '#f1f5f9',
                          color: status?.color || '#64748b',
                          padding: '6px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600',
                          textAlign: 'center',
                          marginBottom: '4px',
                        }}>
                          {status?.icon} {status?.name}
                        </div>
                      )}

                      <Grid>
                        {/* Action Buttons */}
                        {(owner?.id === currentUser?.id || currentUser?.isAdmin) ?
                          <ActionButton
                            style={{ width: '100%' }}
                            type="comment"
                            onClick={() => openModal('comment', lead)}
                          >
                            <FaComment />
                          </ActionButton>
                          : <></>}

                        {/* Delete Button - Admin only */}
                        {currentUser?.isAdmin ? (
                          <ActionButton
                            style={{ width: '100%' }}
                            type="delete"
                            onClick={() => openModal('delete_single', lead)}
                          >
                            <FaTrash />
                          </ActionButton>
                        )
                          :
                          <></>}

                        {/* View Button */}
                        <ViewButton
                          style={{ width: '100%' }} onClick={() => openDetailView(lead)}>
                          <FaEye />
                        </ViewButton>
                      </Grid>
                    </ActionsColumn>
                  </TableCell>
                </TableRow>
              );
            })}
          </LeadsTable>

          {/* Pagination */}
          {hasMoreLeads && (
            <LoadMoreContainer>
              <div>
                <PaginationInfo>
                  Showing {paginatedLeads.length} of {filteredLeads.length} leads
                </PaginationInfo>
                <LoadMoreButton
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? (
                    <>
                      <LoadingSpinner />
                      Loading...
                    </>
                  ) : (
                    <>
                      Load More ({Math.min(10, filteredLeads.length - leadsToShow)} more)
                    </>
                  )}
                </LoadMoreButton>
              </div>
            </LoadMoreContainer>
          )}

          {!hasMoreLeads && filteredLeads.length > 10 && (
            <LoadMoreContainer>
              <PaginationInfo>
                Showing all {filteredLeads.length} leads
              </PaginationInfo>
            </LoadMoreContainer>
          )}
        </div>

        {/* Notification */}
        {notification && (
          <NotificationContainer>
            <NotificationCard type={notification.type}>
              {notification.message}
            </NotificationCard>
          </NotificationContainer>
        )}
      </Container >

      {/* Modals - Rendered using Portal to document.body for full viewport coverage */}
      {
        showModal && ReactDOM.createPortal(
          <Modal>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <CloseButton onClick={closeModal}>
                <FaTimes />
              </CloseButton>
              {modalType === 'comment' && (
                <>
                  <ModalHeader>💬 Add Comment</ModalHeader>
                  <ModalInfo>
                    {/* <strong>📝 Lead Comment</strong><br /> */}
                    Add a comment or note about <br /> this lead for your team.
                  </ModalInfo>
                  <TextArea
                    placeholder="Enter your comment or note here..."
                    value={modalData.comment || ''}
                    onChange={(e) => setModalData(prev => ({ ...prev, comment: e.target.value }))}
                  />
                  <ModalActions>
                    <Button onClick={closeModal}>Cancel</Button>
                    <Button primary onClick={handleModalSubmit}>💬 Add Comment</Button>
                  </ModalActions>
                </>
              )}

              {modalType === 'additional_info' && (
                <>
                  <ModalHeader>Add Additional Information</ModalHeader>
                  <ModalSelect
                    value={modalData.type || ''}
                    onChange={(e) => setModalData(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="">Select Type</option>
                    <option value="email">📧 Email Address</option>
                    <option value="phone">📞 Phone Number</option>
                  </ModalSelect>
                  <Input
                    placeholder={modalData.type === 'email' ? 'Enter email address' : 'Enter phone number'}
                    value={modalData.value || ''}
                    onChange={(e) => setModalData(prev => ({ ...prev, value: e.target.value }))}
                  />
                  <ModalActions>
                    <Button onClick={closeModal}>Cancel</Button>
                    <Button primary onClick={handleModalSubmit}>Add Info</Button>
                  </ModalActions>
                </>
              )}

              {modalType === 'bulk_assign' && (
                <>
                  <ModalHeader>Bulk Assign Leads</ModalHeader>
                  <ModalInfo>
                    {/* <strong>📊 Bulk Assignment</strong><br /> */}
                    Assign {selectedLeads.length} selected leads to a user. This will reassign any already assigned leads to the selected user.
                  </ModalInfo>
                  <ModalSelect
                    value={modalData.ownerId || ''}
                    onChange={(e) => setModalData(prev => ({ ...prev, ownerId: e.target.value }))}
                  >
                    <option value="">🚫 Unassigned</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        👨‍💼 {user.name} ({user.employee_id})
                      </option>
                    ))}
                  </ModalSelect>
                  <ModalActions>
                    <Button onClick={closeModal}>Cancel</Button>
                    <Button primary onClick={handleModalSubmit}>Assign Leads</Button>
                  </ModalActions>
                </>
              )}

              {modalType === 'assign' && (
                <>
                  <ModalHeader>Assign Lead</ModalHeader>
                  <ModalInfo>
                    Assign lead <strong>{selectedLead?.id} - {selectedLead?.name}</strong> to a user.<br />
                    <em>This lead is currently unassigned</em>
                  </ModalInfo>
                  <ModalSelect
                    value={modalData.ownerId || ''}
                    onChange={(e) => setModalData(prev => ({ ...prev, ownerId: e.target.value }))}
                  >
                    <option value="">🚫 Unassigned</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        👨‍💼 {user.name} ({user.employee_id})
                      </option>
                    ))}
                  </ModalSelect>
                  <ModalActions>
                    <Button onClick={closeModal}>Cancel</Button>
                    <Button primary onClick={handleModalSubmit}>Assign Lead</Button>
                  </ModalActions>
                </>
              )}

              {modalType === 'add_lead' && (
                <>
                  <ModalHeader>Add New Lead</ModalHeader>
                  <ModalInfo>
                    {/* <strong>➕ New Lead</strong><br /> */}
                    Add a new lead to the panel.
                  </ModalInfo>
                  {/* label */}
                  <Label>Full Name</Label>
                  <Input
                    placeholder="Full Name"
                    value={modalData.name || ''}
                    onChange={(e) => setModalData(prev => ({ ...prev, name: e.target.value }))}
                  />
                  {/* label */}
                  <Label>Primary Email</Label>
                  <Input
                    type="email"
                    placeholder="Primary Email"
                    value={modalData.email || ''}
                    onChange={(e) => setModalData(prev => ({ ...prev, email: e.target.value }))}
                  />
                  {/* label */}
                  <Label>Primary Phone</Label>
                  <Input
                    type="tel"
                    placeholder="Primary Phone"
                    value={modalData.phone || ''}
                    onChange={(e) => setModalData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                  {/* <ModalSelect
                  value={modalData.source || ''}
                  onChange={(e) => setModalData(prev => ({ ...prev, source: e.target.value }))}
                >
                  <option value="">Select Lead Source</option>
                  {leadSources.map(source => (
                    <option key={source.id} value={source.id}>
                      {source.icon} {source.name}
                    </option>
                  ))}
                </ModalSelect> */}
                  {/* label */}
                  <Label>Tour/Destination</Label>
                  <Input
                    placeholder="Tour/Destination"
                    value={modalData.tourName || ''}
                    onChange={(e) => setModalData(prev => ({ ...prev, tourName: e.target.value }))}
                  />
                  {/* label */}
                  <Label>Number of Passengers</Label>
                  <Input
                    type="number"
                    placeholder="Number of Passengers"
                    value={modalData.numberOfPax || ''}
                    onChange={(e) => setModalData(prev => ({ ...prev, numberOfPax: e.target.value }))}
                  />
                  {/* label */}
                  <Label>Travel Date</Label>
                  <Input
                    type="date"
                    placeholder="Travel Date"
                    value={modalData.travelDate || ''}
                    onChange={(e) => setModalData(prev => ({ ...prev, travelDate: e.target.value }))}
                  />
                  {/* label */}
                  <Label>Message/Requirements</Label>
                  <TextArea
                    placeholder="Message/Requirements..."
                    value={modalData.message || ''}
                    onChange={(e) => setModalData(prev => ({ ...prev, message: e.target.value }))}
                  />
                  <ModalActions>
                    <Button onClick={closeModal}>Cancel</Button>
                    <Button
                      primary
                      onClick={handleModalSubmit}
                      disabled={!modalData.name || !modalData.email || !modalData.phone}
                    >
                      Add Lead
                    </Button>
                  </ModalActions>
                </>
              )}

              {modalType === 'reassign' && (
                <>
                  <ModalHeader>Reassign Lead</ModalHeader>
                  <ModalInfo>
                    Reassign lead <strong>{selectedLead?.id} - {selectedLead?.name}</strong> to a different user.<br />
                    <em>Currently assigned to: <strong>{selectedLead?.owner_name} {selectedLead?.owner_employee_id || 'Unassigned'}</strong></em>
                  </ModalInfo>
                  <ModalSelect
                    value={modalData.ownerId || selectedLead?.owner_id || ''}
                    onChange={(e) => setModalData(prev => ({ ...prev, ownerId: e.target.value }))}
                  >
                    <option value="">🚫 Unassigned</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        👨‍💼 {user.name} ({user.employee_id})
                      </option>
                    ))}
                  </ModalSelect>
                  <ModalActions>
                    <Button onClick={closeModal}>Cancel</Button>
                    <Button primary onClick={handleModalSubmit}>Reassign Lead</Button>
                  </ModalActions>
                </>
              )}

              {modalType === 'delete_single' && (
                <>
                  <ModalHeader>🗑️ Delete Lead</ModalHeader>
                  <ModalInfo>
                    Are you sure you want to delete lead <strong>{selectedLead?.id} - {selectedLead?.customer_name}</strong>?<br />
                    <em>⚠️ This action cannot be undone. All lead data, contacts, comments, and timeline will be permanently deleted.</em>
                  </ModalInfo>
                  <ModalActions>
                    <Button onClick={closeModal}>Cancel</Button>
                    <Button
                      onClick={() => {
                        handleDeleteLead(selectedLead.id);
                        closeModal();
                      }}
                      style={{
                        background: 'linear-gradient(135deg, #f56565, #e53e3e)',
                        color: 'white',
                        border: '2px solid #e53e3e'
                      }}
                    >
                      🗑️ Delete Lead
                    </Button>
                  </ModalActions>
                </>
              )}

              {modalType === 'bulk_delete' && (
                <>
                  <ModalHeader>🗑️ Bulk Delete Leads</ModalHeader>
                  <ModalInfo>
                    Are you sure you want to delete <strong>{selectedLeads.length} selected lead{selectedLeads.length > 1 ? 's' : ''}</strong>?<br />
                    <em>⚠️ This action cannot be undone. All lead data, contacts, comments, and timeline will be permanently deleted.</em>
                  </ModalInfo>
                  <ModalActions>
                    <Button onClick={closeModal}>Cancel</Button>
                    <Button
                      onClick={handleModalSubmit}
                      style={{
                        background: 'linear-gradient(135deg, #f56565, #e53e3e)',
                        color: 'white',
                        border: '2px solid #e53e3e'
                      }}
                    >
                      🗑️ Delete {selectedLeads.length} Lead{selectedLeads.length > 1 ? 's' : ''}
                    </Button>
                  </ModalActions>
                </>
              )}
            </ModalContent>
          </Modal>,
          document.body
        )
      }

      {/* Lead Detail View - Rendered using Portal to document.body for full viewport coverage */}
      {
        showDetailView && detailViewLead && ReactDOM.createPortal(
          <LeadDetailView
            // onUserEdit={() => openModal('user_edit', detailViewLead)}
            lead={detailViewLead}
            onClose={closeDetailView}
            onUpdate={handleLeadUpdate}
            currentUser={currentUser}
            users={users}
          />,
          document.body
        )
      }
    </>
  );
};

export default LeadManagement;